import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { persistQueryClient, PersistedClient } from '@tanstack/query-persist-client-core';
import localforage from 'localforage';
import { isCriticalError, getErrorStatus } from './errors';
import { captureError } from './sentry';

// Initialize localforage instance
const storage = localforage.createInstance({
  name: 'terminus-cache'
});

// Create and configure QueryClient with global error handling
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Capture critical errors to Sentry
      const status = getErrorStatus(error);
      if (isCriticalError(status)) {
        captureError(error as Error, {
          queryKey: query.queryKey,
          type: 'query',
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Capture all mutation errors to Sentry (they represent user actions)
      captureError(error as Error, {
        mutationKey: mutation.options.mutationKey,
        type: 'mutation',
      });
    },
  }),
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60, // 1 minute
      retry: (failureCount, error) => {
        const status = getErrorStatus(error);
        // Don't retry 4xx errors (client errors)
        if (status && status >= 400 && status < 500) {
          return false;
        }
        // Retry 5xx errors up to 2 times
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      throwOnError: (error) => {
        // Throw error to error boundary for critical (5xx) errors
        return isCriticalError(getErrorStatus(error));
      },
    },
    mutations: {
      retry: false, // Don't retry mutations by default
      throwOnError: (error) => {
        // Throw error to error boundary for critical (5xx) errors
        return isCriticalError(getErrorStatus(error));
      },
    },
  }
});

// Create persister
const persister = {
  persistClient: async (client: PersistedClient) => {
    await storage.setItem('TERMINUS_QUERY_CACHE', client);
  },
  restoreClient: async () => {
    return await storage.getItem('TERMINUS_QUERY_CACHE') as PersistedClient;
  },
  removeClient: async () => {
    await storage.removeItem('TERMINUS_QUERY_CACHE');
  }
};

// Initialize persistence
persistQueryClient({
  queryClient,
  persister,
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
  buster: 'v2-success-only',
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => {
      if (query.state.status !== 'success') {
        return false;
      }
      return query.queryKey[0] === 'market_insights' ||
             query.queryKey[0] === 'draftInsights' ||
             query.queryKey[0] === 'sectors';
    }
  }
});
